import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { getCurrencySymbol } from '@/constants/countries';
import { RootState } from '@/redux/store';
import { Destination } from '@/types/parcel/createOrder/create.interface';
import { DeliveryTypes } from '@/types/parcel/createOrder/quotation.interface';
import { handleCountryPrefix } from '@/utils/common';

import type { AddressInputRef } from '../dependencies/AddressInput';
import type { SavedAddress } from '@/types/parcel/userData';

import { CreateOrderFormType, schema } from '../create-order-schema';
import { deliveryTypeChips, getSizeChips, getSizeRanges } from '../dependencies/constants';

interface UseCreateOrderFormProp {
  resetForm: boolean;
  handleHasQuot: () => void;
  getP99: ({ latitude, longitude }: { latitude?: number; longitude?: number }) => Promise<void>;
  handleCoordinatesPoint?: (values: Destination, type: 'origin' | 'destination') => void;
  getQuotation?: (value: CreateOrderFormType) => Promise<void>;
  resetQuotation?: () => void;
  quotData?: DeliveryTypes[] | null;
  loadingQuotation?: boolean;
  onFormChange?: (values: CreateOrderFormType) => void;
}

const useCreateOrderForm = (props: UseCreateOrderFormProp) => {
  const {
    resetForm,
    handleHasQuot,
    getP99,
    handleCoordinatesPoint,
    getQuotation,
    resetQuotation,
    quotData,
    loadingQuotation,
    onFormChange,
  } = props;
  const { profile } = useSelector((state: RootState) => state.parcel);
  const [openSizeCalculator, setOpenSizeCalculator] = useState(false);
  const [selectedCountryPhone, setSelectedCountryPhone] = useState('MX');
  const [showDirectionForm, setShowDirectionForm] = useState({
    inOrigin: false,
    inDestination: false,
  });
  const [showZipCodeInput, setShowZipCodeInput] = useState({
    inOrigin: false,
    inDestination: false,
  });

  const [savedAddressesModalState, setSavedAddressesModalState] = useState<{
    open: boolean;
    targetType: 'origin' | 'destination';
  }>({
    open: false,
    targetType: 'origin',
  });

  const geoSuggestRef = useRef<AddressInputRef | null>(null);
  const geoSuggestDestinationRef = useRef<AddressInputRef | null>(null);
  const limitServices = profile?.data?.configuration?.services;
  const sizeChips = useMemo(
    () => getSizeChips(profile?.data?.configuration?.packageSizes),
    [profile?.data?.configuration?.packageSizes],
  );
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    control,
    getValues,
    watch,
    setValue,
    reset,
    unregister,
  } = useForm<CreateOrderFormType>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    context: {
      country: selectedCountryPhone,
      codMin: limitServices?.cashOnDelivery?.min,
      codMax: limitServices?.cashOnDelivery?.max,
      insMin: limitServices?.insurance?.min,
      insMaX: limitServices?.insurance?.max,
    },
    defaultValues: {
      origin: {
        country: profile?.data?.country || 'MEX',
        showForm: false,
        address: '',
        zipCode: '',
        city: '',
        state: '',
        cologne: '',
        externalNumber: '',
        internalNumber: '',
        reference: '',
      },
      destination: {
        country: profile?.data?.country || 'MEX',
        showForm: false,
        address: '',
        zipCode: '',
        city: '',
        state: '',
        cologne: '',
        externalNumber: '',
        internalNumber: '',
        reference: '',
      },
      sender: {
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
      },
      recipient: {
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
      },
      items: {
        length: '0',
        width: '0',
        height: '0',
        weight: 0,
        peso: 0,
      },
      draft: true,
      saveOriginAddress: false,
      saveDestinationAddress: false,
      internalKey: '',
      options: {
        scheduleNow: false,
      },
      payments: {
        insured: false,
        cashOnDelivery: {
          cod: false,
        },
      },
    },
  });

  //start handlers

  const handleOpenSavedAddresses = (targetType: 'origin' | 'destination') => {
    setSavedAddressesModalState({ open: true, targetType });
  };

  const handleCloseSavedAddresses = () => {
    setSavedAddressesModalState((prev) => ({ ...prev, open: false }));
  };

  const handleSelectSavedAddress = async (savedAddress: SavedAddress) => {
    const target = savedAddressesModalState.targetType;
    const isOrigin = target === 'origin';
    const addressPrefix = isOrigin ? 'origin' : 'destination';
    const contactPrefix = isOrigin ? 'sender' : 'recipient';

    const fullStreet = [
      savedAddress.address,
      savedAddress.street_number ? `No. ${savedAddress.street_number}` : '',
      savedAddress.interior_number ? `Int. ${savedAddress.interior_number}` : '',
    ]
      .filter(Boolean)
      .join(' ');

    const fullReadableAddress = [
      fullStreet || savedAddress.address,
      savedAddress.colony,
      savedAddress.zip_code ? `C.P. ${savedAddress.zip_code}` : '',
      savedAddress.city,
      savedAddress.state,
    ]
      .filter(Boolean)
      .join(', ');

    const displayAddress = fullReadableAddress || savedAddress.address;

    const siteKey = isOrigin ? 'inOrigin' : 'inDestination';
    const isManual = showDirectionForm[siteKey];

    if (isManual) {
      // En modo manual: asigna únicamente el nombre de la calle y los campos específicos
      setValue(`${addressPrefix}.address` as any, savedAddress.address, { shouldValidate: true });
      setValue(`${addressPrefix}.externalNumber` as any, savedAddress.street_number || '', {
        shouldValidate: true,
      });
      setValue(`${addressPrefix}.internalNumber` as any, savedAddress.interior_number || '', {
        shouldValidate: true,
      });
      setValue(`${addressPrefix}.cologne` as any, savedAddress.colony || '', {
        shouldValidate: true,
      });
      setValue(`${addressPrefix}.zipCode` as any, savedAddress.zip_code, { shouldValidate: true });
      setValue(`${addressPrefix}.city` as any, savedAddress.city, { shouldValidate: true });
      setValue(`${addressPrefix}.state` as any, savedAddress.state || '', { shouldValidate: true });
    } else {
      // En modo autocompletado: asigna la dirección completa para el buscador de Google Maps
      setValue(`${addressPrefix}.address` as any, displayAddress, { shouldValidate: true });
      setValue(`${addressPrefix}.externalNumber` as any, savedAddress.street_number || '', {
        shouldValidate: true,
      });
      setValue(`${addressPrefix}.internalNumber` as any, savedAddress.interior_number || '', {
        shouldValidate: true,
      });
      setValue(`${addressPrefix}.cologne` as any, savedAddress.colony || '', {
        shouldValidate: true,
      });
      setValue(`${addressPrefix}.zipCode` as any, savedAddress.zip_code, { shouldValidate: true });
      setValue(`${addressPrefix}.city` as any, savedAddress.city, { shouldValidate: true });
      setValue(`${addressPrefix}.state` as any, savedAddress.state || '', { shouldValidate: true });

      if (isOrigin) {
        geoSuggestRef.current?.setText(displayAddress);
      } else {
        geoSuggestDestinationRef.current?.setText(displayAddress);
      }
    }

    if (savedAddress.country) {
      setValue(`${addressPrefix}.country` as any, savedAddress.country, { shouldValidate: true });
    }
    setValue(`${addressPrefix}.lat` as any, String(savedAddress.latitude), {
      shouldValidate: true,
    });
    setValue(`${addressPrefix}.lng` as any, String(savedAddress.longitude), {
      shouldValidate: true,
    });
    if (savedAddress.references) {
      setValue(`${addressPrefix}.reference` as any, savedAddress.references, {
        shouldValidate: true,
      });
    }

    // Contact
    const country = (savedAddress.country || profile?.data?.country || 'MEX') as any;
    const rawPhone = savedAddress.contact?.phone || '';
    const phone = rawPhone ? handleCountryPrefix({ country, phone: rawPhone }) : '';

    setValue(`${contactPrefix}.firstName` as any, savedAddress.contact?.first_name || '', {
      shouldValidate: true,
    });
    setValue(`${contactPrefix}.lastName` as any, savedAddress.contact?.last_name || '', {
      shouldValidate: true,
    });
    setValue(`${contactPrefix}.phone` as any, phone, { shouldValidate: true });
    setValue(`${contactPrefix}.email` as any, savedAddress.contact?.email || '', {
      shouldValidate: true,
    });

    if (isOrigin) {
      setShowZipCodeInput((prev) => ({ ...prev, inOrigin: false }));
      if (savedAddress.latitude && savedAddress.longitude) {
        getP99({ latitude: savedAddress.latitude, longitude: savedAddress.longitude });
      }
    } else {
      setShowZipCodeInput((prev) => ({ ...prev, inDestination: false }));
    }

    handleCoordinatesPoint?.(
      {
        address: displayAddress,
        lat: String(savedAddress.latitude),
        lng: String(savedAddress.longitude),
      } as Destination,
      target,
    );
  };

  const handleToggleSaveOriginAddress = (checked: boolean) => {
    setValue('saveOriginAddress', checked);
  };

  const handleToggleSaveDestinationAddress = (checked: boolean) => {
    setValue('saveDestinationAddress', checked);
  };

  const handleOpenCloseSizeCalculator = () => {
    setOpenSizeCalculator(!openSizeCalculator);
  };

  const handleShowDirectionForm = (site: 'inOrigin' | 'inDestination') => {
    const isNowManual = !showDirectionForm[site];
    setShowDirectionForm((prev) => ({ ...prev, [site]: isNowManual }));
    const siteProperty = site === 'inOrigin' ? 'origin' : 'destination';
    setValue(`${siteProperty}.showForm` as any, isNowManual);

    // Limpieza completa de los campos del sitio al cambiar de modo
    setValue(`${siteProperty}.address` as any, '');
    setValue(`${siteProperty}.externalNumber` as any, '');
    setValue(`${siteProperty}.internalNumber` as any, '');
    setValue(`${siteProperty}.cologne` as any, '');
    setValue(`${siteProperty}.state` as any, '');
    setValue(`${siteProperty}.city` as any, '');
    setValue(`${siteProperty}.zipCode` as any, '');
    setValue(`${siteProperty}.lat` as any, '');
    setValue(`${siteProperty}.lng` as any, '');
    setValue(`${siteProperty}.reference` as any, '');

    // Limpiar input del buscador de Google Maps
    const refToClear = site === 'inOrigin' ? geoSuggestRef : geoSuggestDestinationRef;
    refToClear.current?.setText('');
    refToClear.current?.selectSuggest(null);

    // Ocultar input extra de CP
    setShowZipCodeInput((prev) => ({ ...prev, [site]: false }));
  };

  const handleZipCodeNotFound = (site: 'inOrigin' | 'inDestination', notFound: boolean) => {
    setShowZipCodeInput((prev) => ({ ...prev, [site]: notFound }));
  };

  const handleChangeSelectedCountryPhone = (value: string) => {
    setSelectedCountryPhone(value);
  };

  const selectDeliveryType = (deliveryType: string) => {
    setValue('deliveryType', deliveryType, { shouldValidate: true, shouldDirty: true });
  };

  const getWeight = (code: string) => {
    const packageSizes = profile?.data?.configuration?.packageSizes;
    const match = packageSizes?.find((data) => data.size.toLowerCase() === code);
    return match?.maxWeight ?? 0;
  };

  const selectSize = (size: string) => {
    setValue('items.size', size, { shouldValidate: true });
    setValue('items.weight', getWeight(size), { shouldValidate: true });
  };

  const setProfileData = async () => {
    const profileData = profile?.data?.profile;
    const companyData = profile?.data?.company;
    const country = profile?.data?.country || 'MEX';

    const address = profileData?.address || companyData?.address || '';
    const firstName = profileData?.firstName || companyData?.name || '';
    const lastName = profileData?.lastName || '';
    const rawPhone = profileData?.phone || companyData?.phone || '';
    const email = profileData?.email || companyData?.email || '';

    const phone = rawPhone ? handleCountryPrefix({ country, phone: rawPhone }) : '';

    await geoSuggestRef?.current?.update(address);
    geoSuggestRef?.current?.selectSuggest(null);
    setValue('sender.firstName', firstName, { shouldValidate: true });
    setValue('sender.lastName', lastName, { shouldValidate: true });
    setValue('sender.phone', phone, { shouldValidate: true });
    setValue('sender.email', email, { shouldValidate: true });
    if (profile?.data?.country) {
      setValue('origin.country', profile.data.country as any, { shouldValidate: true });
    }
    // Limpiar el estado del campo adicional de código postal
    setShowZipCodeInput((prev) => ({ ...prev, inOrigin: false }));
  };

  const currencySymbol = getCurrencySymbol(profile?.data?.country);

  const filterDeliveryTypeChips = useMemo(() => {
    const mapping: Record<string, string> = {
      SMD: 'sameDay',
      SPT: 'sprint',
      '99M': 'express',
      CO2F: 'co2Free',
      RET: 'return',
      NAL: 'national',
    };

    const isPrepaid = profile?.data?.configuration?.paymentType === 'PREPAID';
    const isQuoted = Boolean(quotData && Array.isArray(quotData) && quotData.length > 0);

    const hasOnlyNationalQuote =
      isQuoted &&
      (quotData as DeliveryTypes[]).every(
        (quote) => quote.deliveryTypeCode === 'NAL' || quote.deliveryType === 'national',
      );

    const userAllowed = deliveryTypeChips.filter((chip) => {
      if (chip.value === 'NAL') {
        if (profile?.data?.country !== 'MEX') return false;

        const hasNationalConfigured = Boolean(
          profile?.data?.configuration?.deliveryTypes?.national,
        );
        if (hasNationalConfigured) return true;

        // Excepción: Solo cuentas prepago cuando la cotización recibida es EXCLUSIVAMENTE nacional
        return isPrepaid && hasOnlyNationalQuote;
      }

      const deliveryKey = mapping[chip.value];
      return deliveryKey
        ? profile?.data?.configuration?.deliveryTypes[
            deliveryKey as keyof typeof profile.data.configuration.deliveryTypes
          ]
        : false;
    });

    return userAllowed.map((chip) => {
      if (!isQuoted) return chip;

      const quoteItem = quotData?.find((d) => d.deliveryTypeCode === chip.value);
      if (!quoteItem) {
        return {
          ...chip,
          disabled: true,
          price: undefined,
          subtitle: undefined,
        };
      }

      const priceVal = quoteItem?.prices?.totalWithoutCOD ?? quoteItem?.prices?.flat;
      const addedDays = quoteItem?.eta?.addedDays ?? 0;
      const etaText =
        addedDays === 0 ? 'Mismo día' : `${addedDays} ${addedDays > 1 ? 'días' : 'día'}`;

      return {
        ...chip,
        disabled: false,
        price: priceVal !== undefined ? `${currencySymbol} ${priceVal.toFixed(2)}` : undefined,
        subtitle: etaText,
      };
    });
  }, [
    profile?.data?.configuration?.deliveryTypes,
    profile?.data?.configuration?.paymentType,
    profile?.data?.country,
    quotData,
    currencySymbol,
  ]);

  const isQuoted = Boolean(quotData && Array.isArray(quotData) && quotData.length > 0);

  const visibleChipValues = useMemo(
    () => filterDeliveryTypeChips.map((c) => c.value),
    [filterDeliveryTypeChips],
  );

  // Solo considerar códigos de servicios cotizados que realmente estén visibles en la UI
  const availableQuotedCodes = useMemo(
    () =>
      quotData && Array.isArray(quotData)
        ? quotData
            .map((d) => d.deliveryTypeCode)
            .filter((code) => visibleChipValues.includes(code as any))
        : [],
    [quotData, visibleChipValues],
  );

  // Si la opción seleccionada no tiene cobertura con la nueva cotización, cambiar a la primera disponible visible
  useEffect(() => {
    if (isQuoted) {
      if (availableQuotedCodes.length > 0) {
        const currentSelected = getValues('deliveryType');
        if (!currentSelected || !availableQuotedCodes.includes(currentSelected as any)) {
          selectDeliveryType(availableQuotedCodes[0]);
        }
      } else {
        // Ninguno de los servicios visibles tiene cobertura (ej. solo NAL)
        setValue('deliveryType', '' as any, { shouldValidate: true, shouldDirty: true });
        onFormChange?.({ ...getValues(), deliveryType: '' });
      }
    }
  }, [quotData, availableQuotedCodes, isQuoted]);

  // Sincronizar en tiempo real el estado del formulario con el Summary y el Hook de Orden
  useEffect(() => {
    onFormChange?.({ ...getValues() });
    const subscription = watch(() => {
      onFormChange?.({ ...getValues() });
    });
    return () => subscription.unsubscribe();
  }, [watch, getValues, onFormChange]);

  const selectedDeliveryType = watch('deliveryType');

  const selectedTypeHasCoverage =
    !isQuoted ||
    (Boolean(selectedDeliveryType) && availableQuotedCodes.includes(selectedDeliveryType as any));

  const isNationalSelected = selectedDeliveryType === 'NAL';

  useEffect(() => {
    if (!isNationalSelected) return;

    if (getValues('payments.insured')) {
      setValue('payments.insured', false, { shouldValidate: true, shouldDirty: true });
      setValue('payments.insuredValue', undefined);
      unregister('payments.insuredValue');
    }

    if (getValues('options.scheduleNow')) {
      setValue('options.scheduleNow', false, { shouldValidate: true, shouldDirty: true });
      setValue('options.pickUpAfter', undefined);
      unregister('options.pickUpAfter');
      setValue('draft', false);
    }
  }, [isNationalSelected]);

  const availableCoverageNames = useMemo(() => {
    return filterDeliveryTypeChips
      .filter((c) => availableQuotedCodes.includes(c.value as any))
      .map((c) => c.label)
      .join(', ');
  }, [filterDeliveryTypeChips, availableQuotedCodes]);

  // Auto-cotización cuando origen, destino y tamaño están presentes
  const originAddress = watch('origin.address');
  const originZip = watch('origin.zipCode');
  const originLat = watch('origin.lat');
  const destAddress = watch('destination.address');
  const destZip = watch('destination.zipCode');
  const destLat = watch('destination.lat');
  const packageSize = watch('items.size');
  const isInsured = watch('payments.insured');
  const insuredValue = watch('payments.insuredValue');
  const isCod = watch('payments.cashOnDelivery.cod');
  const codAmount = watch('payments.cashOnDelivery.amount');

  const isReadyToQuote = Boolean(
    originAddress && (originZip || originLat) && destAddress && (destZip || destLat) && packageSize,
  );

  // 1. Cuando cambia la ruta o el tamaño: invalidar tarifas anteriores y cotizar de nuevo
  useEffect(() => {
    if (resetQuotation) {
      resetQuotation();
    }

    if (isReadyToQuote && getQuotation) {
      const currentValues = getValues();
      const timer = setTimeout(() => {
        void getQuotation(currentValues);
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [originAddress, originZip, originLat, destAddress, destZip, destLat, packageSize]);

  // 2. Cuando se activa/desactiva el seguro o pago contra entrega (toggle rápido 150ms)
  useEffect(() => {
    if (isReadyToQuote && getQuotation) {
      const currentValues = getValues();
      const timer = setTimeout(() => {
        void getQuotation(currentValues);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [isInsured, isCod]);

  // 3. Debounce de teclado al escribir el monto de seguro o monto contra entrega (650ms)
  useEffect(() => {
    if (isReadyToQuote && getQuotation && (isInsured || isCod)) {
      const currentValues = getValues();
      const timer = setTimeout(() => {
        void getQuotation(currentValues);
      }, 650);
      return () => clearTimeout(timer);
    }
  }, [insuredValue, codAmount]);

  // start dimention logic

  const calculatedSize = useMemo(() => {
    let max = 0;
    let dimensionSize: ReturnType<typeof getSizeRanges> = [];
    const dimensions = getSizeRanges(profile?.data?.configuration?.packageSizes);
    const result =
      ((parseInt(getValues('items')?.height) ?? 0) *
        (parseInt(getValues('items')?.width) ?? 0) *
        (parseInt(getValues('items')?.length) ?? 0)) /
      5000;
    const bigger = Math.max(result, getValues('items')?.peso);
    dimensions?.forEach((type) => {
      const newMax = Math.max(type.max, max);
      if (newMax !== max) {
        max = newMax;
      }
    });
    if (max < bigger) {
      dimensionSize = (dimensions ?? []).filter((type) => type.max === max);
    } else {
      dimensionSize = (dimensions ?? []).filter((type) => type.max >= bigger && type.min <= bigger);
    }
    return dimensionSize[0];
  }, [
    profile?.data?.configuration?.packageSizes,
    watch('items')?.height,
    watch('items')?.width,
    watch('items')?.length,
    watch('items')?.peso,
  ]);

  const isDisabled = Boolean(
    watch('items').height &&
    watch('items').peso &&
    watch('items').length &&
    watch('items').width &&
    !errors.items,
  );

  // end dimention logic

  useEffect(() => {
    if (resetForm) {
      reset();
      setShowDirectionForm({ inOrigin: false, inDestination: false });
      setShowZipCodeInput({ inOrigin: false, inDestination: false });
      geoSuggestRef?.current?.update('');
      geoSuggestRef?.current?.selectSuggest(null);
      geoSuggestDestinationRef?.current?.update('');
      geoSuggestDestinationRef?.current?.selectSuggest(null);
    }
  }, [resetForm, reset]);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      // Invalidate quotation on any field change
      if (name?.includes('origin') || name?.includes('destination') || name?.includes('items')) {
        handleHasQuot();
      }

      if (name === 'origin.lat' || name === 'origin.lng') {
        const lat = parseFloat(value.origin?.lat ?? '0');
        const lng = parseFloat(value.origin?.lng ?? '0');
        if (lat && lng) {
          getP99({
            latitude: lat,
            longitude: lng,
          });
          handleCoordinatesPoint?.(value.origin as Destination, 'origin');
        }
      }
      if (name === 'destination.lat' || name === 'destination.lng') {
        handleCoordinatesPoint?.(value.destination as Destination, 'destination');
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, getP99, handleCoordinatesPoint, handleHasQuot]);

  return {
    selectDeliveryType,
    register,
    handleSubmit,
    errors,
    control,
    getValues,
    watch,
    selectSize,
    sizeChips,
    handleShowDirectionForm,
    showDirectionForm,
    handleChangeSelectedCountryPhone,
    calculatedSize,
    setValue,
    handleOpenCloseSizeCalculator,
    openSizeCalculator,
    isDisabled,
    setProfileData,
    findAddressComponent,
    geoSuggestRef,
    geoSuggestDestinationRef,
    reset,
    isValid,
    filterDeliveryTypeChips,
    unregister,
    userCountry: profile?.data?.country,
    showZipCodeInput,
    handleZipCodeNotFound,
    insurancePercentage: limitServices?.insurance?.percentage ?? 2,
    savedAddressesModalState,
    handleOpenSavedAddresses,
    handleCloseSavedAddresses,
    handleSelectSavedAddress,
    handleToggleSaveOriginAddress,
    handleToggleSaveDestinationAddress,
    isReadyToQuote,
    hasQuotData: Boolean(quotData && quotData.length > 0),
    loadingQuotation: Boolean(loadingQuotation),
    selectedTypeHasCoverage,
    isNationalSelected,
    availableCoverageNames,
    isQuoted,
  };
};

export default useCreateOrderForm;

const findAddressComponent = (
  components: google.maps.GeocoderAddressComponent[] | undefined,
  type: string,
) => {
  const address = components?.find((component: google.maps.GeocoderAddressComponent) => {
    return component.types.indexOf(type) !== -1;
  });

  return address?.short_name ?? '';
};
